/**
 * New validation error format from backend
 */
export interface ValidationErrorField {
    fieldName?: string;
    message: string;
    args?: Array<string | number>;
    defaultMessage?: string | null;
}

export interface ValidationErrorResponse {
    message: string;
    body: ValidationErrorField[];
}

/**
 * Old validation error format (for backward compatibility)
 */
export interface LegacyValidationErrorResponse {
    message: string;
    body: string;
}

/**
 * Checks if the error response is in the new validation format
 */
export const isNewValidationErrorFormat = (data: unknown): data is ValidationErrorResponse => {
    if (typeof data !== "object" || data === null) {
        return false;
    }

    const obj = data as Record<string, unknown>;

    if (!("message" in obj) || typeof obj.message !== "string") {
        return false;
    }

    if (!("body" in obj) || !Array.isArray(obj.body) || obj.body.length === 0) {
        return false;
    }

    const firstItem = obj.body[0];
    return typeof firstItem === "object" && firstItem !== null && "message" in firstItem;
};

/**
 * Checks if the error response is in the old validation format
 */
export const isLegacyValidationErrorFormat = (data: unknown): data is LegacyValidationErrorResponse => {
    return (
        typeof data === "object" &&
        data !== null &&
        "message" in data &&
        typeof (data as Record<string, unknown>).message === "string" &&
        "body" in data &&
        typeof (data as Record<string, unknown>).body === "string"
    );
};

/**
 * Parse old format validation error body
 * Example: "[field1];[field2, value];" -> [{field: "field1", value: null}, {field: "field2", value: "value"}]
 */
const parseLegacyValidationBody = (body: string): Array<{ field: string; value: string | null }> => {
    const entries = body.split(";").filter((entry) => entry.trim());
    return entries.map((entry) => {
        const match = entry.match(/\[([^\]]+)]/);
        if (match) {
            const parts = match[1].split(",").map((p) => p.trim());
            return {
                field: parts[0] || "",
                value: parts[1] || null,
            };
        }
        return { field: "", value: null };
    });
};

/**
 * Parse old format validation message
 * Example: "error.required;error.invalid;" -> ["error.required", "error.invalid"]
 */
const parseLegacyValidationMessage = (message: string): string[] => {
    return message.split(";").filter((msg) => msg.trim());
};

/**
 * Format validation errors for display
 * Handles both new and old formats
 */
export const formatValidationErrors = (data: unknown, translate: (key: string, args?: Record<string, unknown>) => string): string => {
    // New format
    if (isNewValidationErrorFormat(data)) {
        return data.body
            .map((error: ValidationErrorField) => {
                // Determine which field contains the error key and which contains the field name
                // The error key typically starts with "error." or contains translation key patterns
                const isFieldNameTheErrorKey = error.fieldName?.startsWith("error.") || false;
                const isMessageTheErrorKey = error.message?.startsWith("error.") || false;

                let errorKey: string;

                // Handle both cases: normal and swapped field/message
                if (isMessageTheErrorKey) {
                    // Normal case: message contains the error key
                    errorKey = error.message;
                } else if (isFieldNameTheErrorKey) {
                    // Swapped case: fieldName contains the error key
                    errorKey = error.fieldName ?? "";
                } else {
                    // Fallback: use message as error key
                    errorKey = error.message;
                }

                // Try to translate the error key
                let translatedMessage = translate(errorKey);

                // If translation key doesn't exist and defaultMessage is provided, use it
                if (translatedMessage === errorKey && error.defaultMessage) {
                    translatedMessage = error.defaultMessage;
                }

                // Replace placeholders with args if provided
                if (error.args && error.args.length > 0) {
                    error.args.forEach((arg, index) => {
                        translatedMessage = translatedMessage.replace(`{{${index}}}`, String(arg));
                    });
                }

                return translatedMessage;
            })
            .join("\n");
    }

    // Legacy format
    if (isLegacyValidationErrorFormat(data)) {
        const messages = parseLegacyValidationMessage(data.message);
        const bodyParts = parseLegacyValidationBody(data.body);

        const errors = messages
            .map((msg, index) => {
                const bodyPart = bodyParts[index];
                const translatedMsg = translate(msg);

                if (bodyPart) {
                    const fieldName = bodyPart.field ? `${bodyPart.field}: ` : "";
                    const value = bodyPart.value ? ` (${bodyPart.value})` : "";
                    return `${fieldName}${translatedMsg}${value}`;
                }

                return translatedMsg;
            })
            .filter((msg) => msg)
            .join("\n");

        return errors || data.message;
    }

    // Fallback for other formats
    if (data && typeof data === "object" && "message" in data) {
        const obj = data as Record<string, unknown>;
        if (typeof obj.message === "string") {
            return obj.message;
        }
    }

    return "common.error";
};
